import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { RefusalBox } from './actions';
import { DAYS, refusalOf, type BookingConfig, type BookingSession, type ClosedDate, type Refusal } from './types';

const sKey = (dayOfWeek: number, sessionNo: number) => `${dayOfWeek}:${sessionNo}`;

/** The whole 7×2 grid, closed where the server has nothing. */
function gridFrom(sessions: BookingSession[]): Record<string, BookingSession> {
  const grid: Record<string, BookingSession> = {};
  for (let d = 1; d <= 7; d++) for (let n = 1; n <= 2; n++) grid[sKey(d, n)] = { dayOfWeek: d, sessionNo: n, isOpen: false, openTime: null, closeTime: null };
  for (const s of sessions) grid[sKey(s.dayOfWeek, s.sessionNo)] = s;
  return grid;
}

/** The public read — what the order portal (and a guest) sees. No bearer. */
async function publicConfig(base: string): Promise<BookingConfig> {
  const r = await fetch(`/api/v1${base}/booking-settings`);
  if (!r.ok) throw new Error(r.statusText);
  return (await r.json()) as BookingConfig;
}

/** Booking settings: the switch and limits, the session grid, closed
 * dates — read and written whole (PUT replaces the stored config); the
 * guest view below reads the public endpoint back. */
export function BookingSettings() {
  const { rid, sid } = useScope();
  const base = useBase();
  const [cfg, setCfg] = useState<BookingConfig | null>(null);
  const [grid, setGrid] = useState<Record<string, BookingSession> | null>(null);
  const [guestView, setGuestView] = useState<BookingConfig | null>(null);
  const [newClosed, setNewClosed] = useState({ closedOn: '', label: '' });
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const adopt = useCallback((c: BookingConfig) => {
    setCfg(c);
    setGrid(gridFrom(c.sessions));
  }, []);
  const loadGuestView = useCallback(() => {
    publicConfig(base).then(setGuestView).catch(() => setGuestView(null));
  }, [base]);

  useEffect(() => {
    api.get<BookingConfig>(`${base}/booking-settings`).then(adopt).catch((e) => setRefusal(refusalOf(e, 'Failed to load.')));
    loadGuestView();
  }, [base, adopt, loadGuestView]);

  async function save() {
    if (!cfg || !grid) return;
    setBusy(true);
    try {
      const body: BookingConfig = { settings: cfg.settings, sessions: Object.values(grid), closedDates: cfg.closedDates };
      adopt(await api.put<BookingConfig>(`${base}/booking-settings`, body));
      loadGuestView();
      setRefusal(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setRefusal(refusalOf(e, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  }

  if (!cfg || !grid) return <div className="loading">{refusal?.message ?? 'Loading…'}</div>;
  const s = cfg.settings;
  const setSettings = (patch: Partial<typeof s>) => setCfg({ ...cfg, settings: { ...s, ...patch } });
  const setClosed = (closedDates: ClosedDate[]) => setCfg({ ...cfg, closedDates });
  const patchSession = (key: string, patch: Partial<BookingSession>) => setGrid({ ...grid, [key]: { ...grid[key], ...patch } });
  const copyMonday = () => {
    const next = { ...grid };
    for (let d = 2; d <= 7; d++) for (let n = 1; n <= 2; n++) next[sKey(d, n)] = { ...grid[sKey(1, n)], dayOfWeek: d };
    setGrid(next);
  };
  const num = (v: string): number => (v === '' ? 0 : Number(v));

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline"><Link to={`/r/${rid}/s/${sid}/bookings`}>Bookings</Link> · Settings</div>
          <h1>Booking settings</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--ok)', fontSize: 13.5, fontWeight: 700 }} data-saved>Saved ✓</span>}
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save settings'}</button>
        </div>
      </div>
      <RefusalBox refusal={refusal} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="panel" data-section="availability">
          <h2>Availability</h2>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, padding: '12px 0' }}>
            <input type="checkbox" aria-label="Accept online bookings" checked={s.enabled} onChange={(e) => setSettings({ enabled: e.target.checked })} />
            <b style={{ fontSize: 14 }}>Accept online bookings</b>
            <span style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>Guests book from the website while this is on; staff can always enter a walk-in.</span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div className="field"><label>Max guests per booking</label>
              <input className="input" type="number" min={1} aria-label="Max guests per booking" value={s.maxPaxPerBooking} onChange={(e) => setSettings({ maxPaxPerBooking: num(e.target.value) })} /></div>
            <div className="field"><label>Capacity per session</label>
              <input className="input" type="number" min={1} placeholder="No limit" aria-label="Capacity per session" value={s.capacityPerSession ?? ''}
                onChange={(e) => setSettings({ capacityPerSession: e.target.value === '' ? null : num(e.target.value) })} /></div>
            <div className="field"><label>Lead time (minutes)</label>
              <input className="input" type="number" min={0} aria-label="Lead time (minutes)" value={s.leadMinutes} onChange={(e) => setSettings({ leadMinutes: num(e.target.value) })} /></div>
            <div className="field"><label>Book ahead (days)</label>
              <input className="input" type="number" min={0} aria-label="Book ahead (days)" value={s.scheduleAheadDays} onChange={(e) => setSettings({ scheduleAheadDays: num(e.target.value) })} /></div>
          </div>
        </div>

        <div className="panel" data-section="sessions">
          <h2 style={{ display: 'flex', alignItems: 'center' }}>
            Sessions
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={copyMonday}><Copy size={14} /> Copy Monday to every day</button>
          </h2>
          <table className="tbl" style={{ marginTop: 6 }}>
            <thead><tr><th>Day</th><th>Session 1</th><th>Session 2</th></tr></thead>
            <tbody>
              {DAYS.map((day, i) => (
                <tr key={day} style={{ cursor: 'default' }}>
                  <td style={{ fontWeight: 700 }}>{day}</td>
                  {[1, 2].map((no) => {
                    const key = sKey(i + 1, no);
                    const w = grid[key];
                    const name = `${day} session ${no}`;
                    return (
                      <td key={no} data-session={key} data-open={w.isOpen}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                            <input type="checkbox" aria-label={`${name} open`} checked={w.isOpen} onChange={(e) => patchSession(key, { isOpen: e.target.checked })} />
                            Open
                          </label>
                          <input className="input" type="time" aria-label={`${name} opens`} disabled={!w.isOpen} value={w.openTime ?? ''}
                            style={{ width: 110, opacity: w.isOpen ? 1 : 0.4 }} onChange={(e) => patchSession(key, { openTime: e.target.value || null })} />
                          <span style={{ color: 'var(--ink-3)' }}>–</span>
                          <input className="input" type="time" aria-label={`${name} closes`} disabled={!w.isOpen} value={w.closeTime ?? ''}
                            style={{ width: 110, opacity: w.isOpen ? 1 : 0.4 }} onChange={(e) => patchSession(key, { closeTime: e.target.value || null })} />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel" data-section="closed-dates">
          <h2>Closed dates</h2>
          {cfg.closedDates.length === 0 && <div className="loading">No closed dates.</div>}
          {cfg.closedDates.map((d) => (
            <div key={d.closedOn} className="rowline" data-closed={d.closedOn}>
              <span className="tm">{d.closedOn}</span>
              <div className="grow"><span className="sub">{d.label ?? ''}</span></div>
              <button className="btn btn-danger btn-sm" aria-label={`Remove ${d.closedOn}`} onClick={() => setClosed(cfg.closedDates.filter((x) => x.closedOn !== d.closedOn))}>
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
            <div className="field"><label>Date</label>
              <input className="input" type="date" aria-label="Closed date" value={newClosed.closedOn} onChange={(e) => setNewClosed({ ...newClosed, closedOn: e.target.value })} /></div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}><label>Label</label>
              <input className="input" placeholder="Private function…" aria-label="Closed date label" value={newClosed.label} onChange={(e) => setNewClosed({ ...newClosed, label: e.target.value })} /></div>
            <button className="btn btn-outline" disabled={!newClosed.closedOn || cfg.closedDates.some((x) => x.closedOn === newClosed.closedOn)}
              onClick={() => {
                setClosed([...cfg.closedDates, { closedOn: newClosed.closedOn, label: newClosed.label.trim() || null }]);
                setNewClosed({ closedOn: '', label: '' });
              }}>
              <Plus size={16} /> Add closed date
            </button>
          </div>
          <div style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 10 }}>Changes apply when you save — the saved list replaces the stored one.</div>
        </div>

        <GuestView cfg={guestView} />
      </div>
    </>
  );
}

/** What a guest sees: the public read of the saved config, in words. */
function GuestView({ cfg }: { cfg: BookingConfig | null }) {
  if (!cfg) return null;
  const s = cfg.settings;
  const open = cfg.sessions.filter((w) => w.isOpen).sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.sessionNo - b.sessionNo);
  return (
    <div className="panel" data-section="guest-view" data-enabled={s.enabled}>
      <h2>What guests see</h2>
      <div className="rowline">
        <div className="grow">
          <b data-guest="status">{s.enabled ? 'Taking online bookings' : 'Not taking online bookings'}</b>
          <span className="sub" data-guest="limits">
            Up to {s.maxPaxPerBooking} guests · from {s.leadMinutes} min ahead · up to {s.scheduleAheadDays} days ahead
            {s.capacityPerSession ? ` · ${s.capacityPerSession} covers a session` : ''}
          </span>
        </div>
      </div>
      {DAYS.map((day, i) => {
        const ws = open.filter((w) => w.dayOfWeek === i + 1);
        return (
          <div key={day} className="rowline" data-guest-day={i + 1}>
            <span className="tm" style={{ minWidth: 90 }}>{day}</span>
            <div className="grow"><span className="sub">{ws.length ? ws.map((w) => `${w.openTime}–${w.closeTime}`).join(', ') : 'No bookings'}</span></div>
          </div>
        );
      })}
      {cfg.closedDates.map((d) => (
        <div key={d.closedOn} className="rowline" data-guest-closed={d.closedOn}>
          <span className="tm" style={{ minWidth: 90 }}>{d.closedOn}</span>
          <div className="grow"><span className="sub">Closed{d.label ? ` — ${d.label}` : ''}</span></div>
        </div>
      ))}
    </div>
  );
}
